import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import Header from './components/Header3';
import Footer from './components/Footer3';

const Contact3 = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }, 3000);
    };

    const inputBaseClass = "w-full px-4 py-3.5 bg-[#FFFFFF] border border-[#E8E0D8] text-[#1A1A1A] text-sm outline-none transition-colors duration-300 focus:border-[#722F37] placeholder-[#9CA3AF]";

    const contactInfo = [
        { icon: Phone, title: 'Call Us', value: '+91 98765 43210', subtitle: 'Mon-Sat, 10AM-7PM IST' },
        { icon: Mail, title: 'Email Us', value: 'hello@vastra.com', subtitle: 'We reply within 24 hours' },
        { icon: MapPin, title: 'Visit Us', value: 'Heritage Lane, Jaipur', subtitle: 'Rajasthan 302001, India' },
    ];

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');`}</style>
            <div style={{ fontFamily: "'Lato', sans-serif" }} className="min-h-screen bg-[#FFFDF7]">
                <Header />
                <main>
                    {/* Hero */}
                    <section className="pt-24 pb-12 lg:pb-16 px-4 text-center" style={{ backgroundColor: '#FFF9F0', borderBottom: '1px solid #E8E0D8' }}>
                        <div className="max-w-2xl mx-auto">
                            <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B' }}>Get in Touch</p>
                            <h1 style={{ fontFamily: "'Playfair Display', serif" }} className="text-3xl lg:text-5xl font-bold text-[#722F37] mb-4">Contact Us</h1>
                            <p className="text-sm lg:text-base" style={{ color: '#6B7280' }}>We would love to hear from you. Reach out for bespoke consultations, enquiries, or just to say hello.</p>
                        </div>
                    </section>

                    {/* Contact Cards */}
                    <section className="py-12 lg:py-16 px-4">
                        <div className="max-w-[1000px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
                            {contactInfo.map((info, idx) => (
                                <div key={idx} className="text-center p-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E0D8' }}>
                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(114,47,55,0.05)', border: '1px solid rgba(114,47,55,0.1)' }}>
                                        <info.icon size={22} style={{ color: '#722F37' }} />
                                    </div>
                                    <h3 className="text-sm font-bold uppercase tracking-wider mb-1" style={{ color: '#722F37' }}>{info.title}</h3>
                                    <p className="text-sm font-bold mb-1" style={{ color: '#1A1A1A' }}>{info.value}</p>
                                    <p className="text-xs" style={{ color: '#6B7280' }}>{info.subtitle}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Contact Form */}
                    <section className="pb-16 lg:pb-24 px-4">
                        <div className="max-w-[700px] mx-auto p-6 lg:p-10" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E0D8' }}>
                            <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl font-bold text-[#722F37] mb-6 text-center">Send us a Message</h2>

                            {submitted ? (
                                <div className="text-center py-12">
                                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(46,204,113,0.1)' }}>
                                        <MessageCircle size={28} style={{ color: '#2ECC71' }} />
                                    </div>
                                    <h3 className="text-lg font-bold mb-2" style={{ color: '#722F37' }}>Message Sent!</h3>
                                    <p className="text-sm" style={{ color: '#6B7280' }}>Thank you for reaching out. We will get back to you within 24 hours.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div><label className="block text-sm font-bold text-[#1A1A1A] mb-1">Name</label><input name="name" value={formData.name} onChange={handleInputChange} required placeholder="Your name" className={inputBaseClass} /></div>
                                        <div><label className="block text-sm font-bold text-[#1A1A1A] mb-1">Email</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="your@email.com" className={inputBaseClass} /></div>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div><label className="block text-sm font-bold text-[#1A1A1A] mb-1">Phone</label><input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" className={inputBaseClass} /></div>
                                        <div><label className="block text-sm font-bold text-[#1A1A1A] mb-1">Subject</label><input name="subject" value={formData.subject} onChange={handleInputChange} required placeholder="How can we help?" className={inputBaseClass} /></div>
                                    </div>
                                    <div><label className="block text-sm font-bold text-[#1A1A1A] mb-1">Message</label><textarea name="message" value={formData.message} onChange={handleInputChange} required rows={5} placeholder="Tell us more..." className={inputBaseClass} style={{ resize: 'vertical' }} /></div>
                                    <button type="submit" className="w-full py-4 text-sm font-bold tracking-[0.15em] uppercase inline-flex items-center justify-center gap-2 transition-all duration-300" style={{ backgroundColor: '#722F37', color: '#FFFFFF' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#5A1F2A'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#722F37'; }}>
                                        <Send size={16} /> Send Message
                                    </button>
                                </form>
                            )}
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default Contact3;
